import { useCallback, useMemo } from "react";
import { useBrandLoaderData } from "../../../routes/root/rootLoader.ts";
import { createVstStoreUrl } from "../../utils/vstStore.ts";
import { getAuthenticatedRedirectLink } from "../../../routes/user/redirect/redirectHelpers.ts";
import { License } from "../../api/decimal/common-types/manageAssets.ts";

type UrlParams = Record<string, string>;

function createAuthVstStoreUrl(
  ...params: Parameters<typeof createVstStoreUrl>
) {
  return getAuthenticatedRedirectLink(createVstStoreUrl(...params));
}

function getHostname(url: string) {
  const urlObj = new URL(url);
  return urlObj.hostname;
}

export enum StoreType {
  None = "none",
  VitalSource = "vitalsource",
  VitalSourceWhiteLabel = "vitalsource-whitelabel",
  ThirdParty = "third-party",
}

export default function useStoreLinks() {
  const brand = useBrandLoaderData();
  const url = brand.Store.url;
  const isStoreEnabled = brand.Store.enabled;
  const isVstStore = brand.Store.enabled && brand.Store.vstStore;
  const storeType = isVstStore
    ? getHostname(url) !== "www.vitalsource.com"
      ? StoreType.VitalSourceWhiteLabel
      : StoreType.VitalSource
    : isStoreEnabled && brand.Store.url
    ? StoreType.ThirdParty
    : StoreType.None;

  const makeBasicStoreURL = useCallback(
    (utmParams?: UrlParams) => {
      if (!isStoreEnabled) {
        return undefined;
      }
      return isVstStore ? createAuthVstStoreUrl(url, "/", utmParams) : url;
    },
    [url, isVstStore, isStoreEnabled]
  );

  const makeProductUrl = useCallback(
    (vbid: string, params: UrlParams = {}) => {
      return isVstStore
        ? createAuthVstStoreUrl(url, "/show/" + vbid, {
            ...params,
            term: vbid,
          })
        : undefined;
    },
    [isVstStore, url]
  );

  const makeStoreSearchUrl = useCallback(
    (query: string, params: UrlParams = {}) => {
      return isVstStore
        ? createAuthVstStoreUrl(url, "/textbooks", {
            ...params,
            term: query,
          })
        : undefined;
    },
    [isVstStore, url]
  );

  const makeRenewalUrl = useCallback(
    (vbid: string, utmParams?: Record<string, string>) => {
      return makeProductUrl(vbid, { ...utmParams, renewal: "true" });
    },
    [makeProductUrl]
  );

  const isStoreAsset = useCallback(
    (license: License) => {
      return (
        isVstStore &&
        (license.intendedUse === "code-api" ||
          license.intendedUse === "vst store")
      );
    },
    [isVstStore]
  );

  return useMemo(
    () => ({
      makeBasicStoreURL,
      makeRenewalUrl,
      isStoreEnabled,
      isVstStore,
      storeType,
      isStoreAsset,
      makeStoreSearchUrl,
    }),
    [
      makeBasicStoreURL,
      isStoreEnabled,
      isVstStore,
      storeType,
      makeRenewalUrl,
      isStoreAsset,
      makeStoreSearchUrl,
    ]
  );
}
